import { create } from "zustand"
import { useEffect, useMemo, useRef } from "react";
import {Formik, Form, FormikValues, FieldArray} from 'formik'
import * as Yup from 'yup'
import { filter, pickBy } from "lodash";
import { doFormSubmit, isPopulatedArray } from "../../../../components/helpers";
import { useQueryClient } from "react-query";
import { FormikFileUpload, FormikTextInput, StatusErrorMessage } from "../../../../components/widgets";
import { SubmitButtonRow } from "../../../../components/layout";

import { Modal } from "bootstrap";
import { ApplicationInspection } from "../../../core/applications";
import { ID, KTSVG } from "../../../../../_metronic/helpers";

interface InspectionReuploadModalState {
  show: boolean,
  data?: ApplicationInspection
  toggleShow: (show: boolean) => void
  setData: (data: ApplicationInspection) => void
  clear: () => void
}

export const useInspectionReuploadModalState = create<InspectionReuploadModalState>()((set) => ({
  show: false,
  data: undefined,
  toggleShow: (show: boolean) => set({show}),
  setData: (data: ApplicationInspection) => set({ data }),
  clear: () => set({ data: undefined }),
}));

const samplingResultSchema = Yup.object({
  id: Yup.number(),
  surveillanceAttachmentId: Yup.number().integer().label('Surveillance Test Result'),
  surveillanceAttachmentIdAttachmentName: Yup.string().trim(),
  testReportReference: Yup.string().trim().label('Surveillance Report Reference'),
});

const InspectionReuploadSchema:any = Yup.object().shape({
  inspectionResult: Yup.number().integer().required('Completed Audit Kit'),
  inspectionResultAttachmentName: Yup.string().trim(),
  completedInspectionCOC: Yup.number().integer().required('Completed CoC'),
  completedInspectionCOCAttachmentName: Yup.string().trim(),  
  samplingForm: Yup.number().integer().required('Sampling Form'),
  samplingFormAttachmentName: Yup.string().trim(),  
  ncr: Yup.number().integer().required('Non-Conformity Report'),
  ncrAttachmentName: Yup.string().trim(),  
  samplings: Yup.array().of(samplingResultSchema).label('Samplings')
})


const initSamplingResultValues = {
  id: 0,
  surveillanceAttachmentId: 0,
  surveillanceAttachmentIdAttachmentName: '',
  testReportReference: '',
};

const INSPECTION_FILE_FIELDS = [
  "inspectionResult",
  "completedInspectionCOC",
  "samplingForm",
  "ncr",
];

type Props = {
  appID : ID
}

export const InspectionReuploadModal = ({appID} : Props) => {

  const queryClient = useQueryClient();

  const modalRef = useRef<HTMLDivElement | null>(null);

  const { show, data, toggleShow} = useInspectionReuploadModalState(state => state);

  const initValues = useMemo(() => {
    
    let initVals = {     
      inspectionResult: 0,
      inspectionResultAttachmentName: '',
      completedInspectionCOC: 0,
      completedInspectionCOCAttachmentName: '',
      samplingForm: 0,
      samplingFormAttachmentName: '',
      ncr: 0,
      ncrAttachmentName: '',
      samplings:[]
    };

    if (! data) return initVals;

    INSPECTION_FILE_FIELDS.forEach(key => {
      // @ts-ignore
      if (! (data.hasOwnProperty(key) && data[key]?.id)) return;
      // @ts-ignore
      Object.assign(initVals, { [key] : data[key].id, [`${key}AttachmentName`] : data[key].fileName, })
    });

    if (data?.samplings && isPopulatedArray(data.samplings)) {
      Object.assign(initVals, {samplings: data.samplings.map(o => ({
        id: o.id,
        surveillanceAttachmentId: o?.surveillanceTestResult && o.surveillanceTestResult?.id ? o.surveillanceTestResult.id : 0,
        surveillanceAttachmentIdAttachmentName: o?.surveillanceTestResult && o.surveillanceTestResult?.fileName ? o.surveillanceTestResult.fileName : '',
        testReportReference: o?.testReportReference ? o.testReportReference : '', 
      }))})
    }

    return initVals;

  }, [data]);

  const doSubmit = async (values: any, actions: FormikValues) => {
    const { setStatus, setSubmitting } = actions;

    if (! (data)) {
      return;
    }
    
    let payload = { 
      id: data.id,      
      ...pickBy(values, (value, key) => value && INSPECTION_FILE_FIELDS.includes(key)),
    };

    let samplings = filter(values.samplings, o => (o.surveillanceAttachmentId));
    if (isPopulatedArray(samplings)) {
      Object.assign(payload, {samplings});
    }

    await doFormSubmit('ApplicationInspectionServlet', payload, setStatus, setSubmitting, () => {
      toggleShow(false);
      queryClient.invalidateQueries(['GETAPPLICATION', `${appID ? appID : 0}`]);
      window.location.reload();
    });
  
  }

  useEffect(() => {
    if (! (modalRef && modalRef.current)) return;
    const modalEle = modalRef.current;    
    if (show) {
      const bsModal = new Modal(modalEle, {
        backdrop: false,
        keyboard: false
      })
      bsModal.show()                
    } else {
      const bsModal= Modal.getInstance(modalEle)
      if (bsModal) bsModal.hide()            
    }      
  }, [show, modalRef]);


  if (! (appID && show)) {
    return null;
  }
  
  return (
    <div className="modal" ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" aria-labelledby="staticBackdropLabel" aria-hidden="true">
      <div className="modal-dialog ">
        <div className="modal-content min-w-550px">
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="staticBackdropLabel">Reupload Inspection Reports</h1>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => toggleShow(false)}></button>
          </div>
          <div className="modal-body">
            <Formik
              initialValues={initValues}
              validationSchema={InspectionReuploadSchema}
              enableReinitialize={true}
              onSubmit={doSubmit}   
            >
            {({status, values, touched, errors}) => (
              <Form id='kt_modal_note_dialog_form' className='form' noValidate>
                <div className='d-flex flex-column p-4' >                          
                  <div className="row mb-2">
                    <FormikFileUpload size="12" label="Completed Audit Kit :" name="inspectionResult" acceptFiles="UserFileFormats" />
                  </div>                                   
                  <div className="row mb-2">
                    <FormikFileUpload size="12" label="Completed Draft CoC :" name="completedInspectionCOC" acceptFiles="UserFileFormats" />
                  </div>                     
                  <div className="row mb-2">
                    <FormikFileUpload size="12" label="Sampling Form :" name="samplingForm" acceptFiles="UserFileFormats" />
                  </div>                     
                  <div className="row mb-2">
                    <FormikFileUpload size="12" label="Non-Conformity Report :" name="ncr" acceptFiles="UserFileFormats" />
                  </div>                     
                  <FieldArray
                    name="samplings"
                    render={ rowHelpers => (
                      <>
                      <div className="row mt-2">                        
                        <div className="col-6 fs-7 fw-bold">Surveillance Test Result</div>
                        <div className="col-5 fs-7 fw-bold">Test Report Reference</div>
                        <div className="col-1 text-center">
                          <button
                            type="button"
                            className="siteBtn btn btn-light-success text-success btn-active-success px-2 py-1 lh-0 me-3"
                            onClick={() => rowHelpers.push( { ...initSamplingResultValues } )}              
                            title="Click to Test Result"                
                          >
                            <KTSVG path='/media/icons/duotune/arrows/arr017.svg' className='svg-icon m-0' />
                          </button>                    
                        </div>
                      </div>
                      <div className='separator border-gray-200 my-1'></div>
                      <div className="scroll-y mh-150px -me-4">
                      { (values?.samplings) && values.samplings.map((row: any, idx: number) => (
                      <div key={`sample${idx}`} className="row mb-2">
                        <FormikFileUpload size="6" label="Test Result" name={`samplings.${idx}.surveillanceAttachmentId`} acceptFiles="UserFileFormats" />
                        <FormikTextInput size="5" label="" name={`samplings.${idx}.testReportReference`} />
                        <div className="col-1">
                          <div 
                            className="rounded bg-light-danger bg-hover-danger text-hover-white w-24px" 
                            onClick={ () => rowHelpers.remove(idx)}
                            title="Delete Test Result"
                          >
                            <KTSVG path='/media/icons/duotune/arrows/arr088.svg' className='svg-icon-1' />
                          </div>                
                        </div>        
                      </div>
                      ))}
                      </div>
                      {(touched?.samplings && errors?.samplings) && 
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block fs-7'>
                          <span role='alert'>{JSON.stringify(errors.samplings)}</span>
                        </div>
                      </div>
                      }
                      </>
                    )}
                  />                                                   
                  <StatusErrorMessage status={status} />
                  <SubmitButtonRow doClose={() => toggleShow(false)} cancelText="Cancel" submitText="Save" />
                </div>
              </Form>
            )}
            </Formik>
          </div>                
        </div>
      </div>
    </div>
  )
}